import { HttpEvent, HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { ApiErrorResponse } from '../../models/api-response.model';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { LoggerService } from '../logger.service';
import { SKIP_ERROR_TOAST } from '../../http/http-context.tokens';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const toast = inject(MessageService);
  const translate = inject(TranslateService);
  const logger = inject(LoggerService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse): Observable<HttpEvent<unknown>> => {
      const skipToast = req.context.get(SKIP_ERROR_TOAST);

      let errorMessage = translate.instant('errors.unexpected');
      const detailErrors = error.error?.error?.details?.errors;
      const detailsMessage = Array.isArray(detailErrors)
        ? detailErrors
            .map((e: { message?: string; path?: string[] }) => {
              const field = Array.isArray(e.path) ? e.path.join('.') : '';
              return field ? `${field}: ${e.message ?? ''}` : (e.message ?? '');
            })
            .filter(Boolean)
            .join(', ')
        : '';

      // Parse Strapi error format
      if (error.error && typeof error.error === 'object' && 'error' in error.error) {
        const apiError: ApiErrorResponse = error.error as ApiErrorResponse;
        errorMessage = apiError.error.message || errorMessage;

        logger.error('API Error', apiError, { url: req.url, status: error.status });
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Handle specific status codes
      switch (error.status) {
        case 401:
          // Unauthorized - redirect to login
          router.navigate(['/login'], {
            queryParams: {
              returnUrl: router.url.startsWith('/login') ? '/' : router.url,
            },
          });
          errorMessage = translate.instant('errors.unauthorized');
          break;

        case 403:
          // Forbidden
          errorMessage = translate.instant('errors.forbidden');
          break;

        case 404:
          errorMessage = translate.instant('errors.notFound');
          break;

        case 422:
          // Validation error
          errorMessage = detailsMessage || errorMessage;
          break;

        case 500:
          errorMessage = detailsMessage || errorMessage;
          logger.error('Internal API Error', error, { url: req.url, status: error.status });
          break;
        case 502:
        case 503:
          errorMessage = translate.instant('errors.serviceUnavailable');
          break;
      }

      if (!skipToast) {
        toast.add({
          severity: 'error',
          summary: translate.instant('errors.error'),
          detail: errorMessage,
          life: 3000,
        });
      }

      // Re-throw error for component handling if needed
      return throwError(() => error);
    }),
  );
};
